import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import * as systemSchemaActions from '../actions/system.actions';
import {
	getAllSystemsSchemas,
	getSystemSchema,
} from '../services/system.service';
import { SagaIterator } from 'redux-saga';
import { systemSchemaSelector, systemSchemaSelectorByType } from '../selectors';

/**
 * Saga to get a specific system schema.
 * @internal
 * @param action - The action containing the system type payload.
 */
export function* getSystemSchemaSaga(
	action: ReturnType<typeof systemSchemaActions.getSystemSchemaAction>,
): SagaIterator {
	const systemType = action.payload;
	try {
		const existingSystemSchema: ReturnType<
			typeof systemSchemaSelectorByType
		> = yield select(systemSchemaSelectorByType, systemType);
		if (existingSystemSchema) {
			return;
		}

		const system: Awaited<ReturnType<typeof getSystemSchema>> = yield call(
			getSystemSchema,
			{ systemType },
		);

		if (system) {
			yield put(systemSchemaActions.getSystemSchemaResolved(system));
		} else {
			console.error(`System Type not found ${systemType}`);
			yield put(systemSchemaActions.getSystemSchemaRejected());
		}
	} catch (error) {
		console.error(`System Type not found ${systemType}`, { error });
		yield put(systemSchemaActions.getSystemSchemaRejected());
	}
}

/**
 * Root saga to watch for getSystemSchemaAction.
 * @internal
 */
export function* systemSchemaSaga() {
	yield takeEvery(
		systemSchemaActions.getSystemSchemaAction,
		getSystemSchemaSaga,
	);
}

/**
 * Saga to get all system schemas.
 * @internal
 */
export function* getAllSystemsSchemaSaga(): SagaIterator {
	try {
		const existingSystemSchemas = yield select(systemSchemaSelector);
		if (Object.keys(existingSystemSchemas).length > 0) {
			return;
		}

		const systems: Awaited<ReturnType<typeof getAllSystemsSchemas>> =
			yield call(getAllSystemsSchemas);

		if (systems) {
			yield put(systemSchemaActions.getAllSystemsSchemaResolved(systems));
		} else {
			console.error('No System Types found');
			yield put(systemSchemaActions.getAllSystemsSchemaRejected());
		}
	} catch (error) {
		console.error('No System Types found', { error });
		yield put(systemSchemaActions.getAllSystemsSchemaRejected());
	}
}

/**
 * Root saga to watch for getAllSystemsSchemaAction.
 * @internal
 */
export function* allSystemsSchemaSaga() {
	yield takeLatest(
		systemSchemaActions.getAllSystemsSchemaAction,
		getAllSystemsSchemaSaga,
	);
}
